import { FORM_MAX_WIDTH, centeredContent } from '@/components/layout/content-width';
import { KeyboardAwareScreenScroll } from '@/components/layout/keyboard-aware-screen-scroll';
import { PageTitle } from '@/components/seo/page-title';
import { Text } from '@/components/ui/text';
import { Image } from 'expo-image';
import React from 'react';
import { useWindowDimensions, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const LOGO = require('@/assets/images/flavrly-app-logo-dark.svg');

/**
 * The logo is decorative (`accessible={false}`), so the space it takes has to be
 * earned. `full` is for the front door -- login, two fields, room to breathe.
 * `compact` is for every screen carrying a real form; a fixed 200px lockup there
 * pushed the submit button below the fold on small devices.
 */
const LOGO_SIZES = {
  full: { ratio: 0.22, min: 96, max: 180 },
  compact: { ratio: 0.12, min: 64, max: 104 },
} as const;

export type AuthLogoSize = keyof typeof LOGO_SIZES;

interface AuthScreenLayoutProps {
  title: string;
  heading?: string;
  subheading?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  logo?: AuthLogoSize;
  /**
   * Social sign-in buttons, rendered above the form with an "or" divider. When
   * omitted the divider is not drawn.
   */
  socialSlot?: React.ReactNode;
}

export function AuthScreenLayout({
  title,
  heading,
  subheading,
  children,
  footer,
  logo = 'compact',
  socialSlot,
}: AuthScreenLayoutProps) {
  const { height } = useWindowDimensions();
  const { ratio, min, max } = LOGO_SIZES[logo];
  const logoHeight = Math.round(Math.max(min, Math.min(max, height * ratio)));

  return (
    <SafeAreaView className="flex-1" edges={['top', 'left', 'right']} testID="auth-safe-area">
      <PageTitle title={title} />
      <KeyboardAwareScreenScroll
        className="flex-1"
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: 'center',
          paddingHorizontal: 20,
          paddingVertical: 24,
        }}
        testID="auth-scroll"
      >
        {/* Shared with the `(setup)` group so the form column never changes
            width between sign-up and the last onboarding step. */}
        <View testID="auth-content-column" style={centeredContent(FORM_MAX_WIDTH)} className="gap-8">
          <View className="items-center gap-2">
            <Image
              source={LOGO}
              style={{ width: '100%', height: logoHeight }}
              contentFit="contain"
              accessible={false}
              testID="auth-logo"
            />
            <Text className="text-xs tracking-widest text-warm-gray-300">
              EAT LIKE A LOCAL
            </Text>
            {heading && (
              <Text className="text-3xl font-bold text-center text-warm-gray-100 mt-2">
                {heading}
              </Text>
            )}
            {subheading && (
              <Text className="text-base text-center text-warm-gray-300">
                {subheading}
              </Text>
            )}
          </View>

          {socialSlot && (
            <View className="gap-5" testID="auth-social-slot">
              {socialSlot}
              <View className="flex-row items-center gap-3">
                <View className="flex-1 h-px bg-border" />
                <Text className="text-xs tracking-wide text-warm-gray-300">or</Text>
                <View className="flex-1 h-px bg-border" />
              </View>
            </View>
          )}

          {children}
          {footer}
        </View>
      </KeyboardAwareScreenScroll>
    </SafeAreaView>
  );
}
